import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ExternalLink, Check, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { useOAuth } from "@/hooks/useOAuth";
import { useOAuthPersistence } from "@/hooks/useOAuthPersistence";

interface TikTokConnectionProps {
	value: string;
	onChange: (value: string) => void;
	connected: boolean;
	onConnect: (username?: string) => void;
	onDisconnect: () => void;
	loading?: boolean;
}

export function TikTokConnection({
	value,
	onChange,
	connected,
	onConnect,
	onDisconnect,
	loading = false
}: TikTokConnectionProps) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [tiktokUsername, setTiktokUsername] = useState("");
	const [isPlatformConfigured, setIsPlatformConfigured] = useState(false);
	const { isConnecting, connect, disconnect, isPlatformConfigured: checkPlatform } = useOAuth();
	const { isConnected: isPersistedConnected, getConnection, disconnect: persistDisconnect } = useOAuthPersistence();

	useEffect(() => {
		setIsPlatformConfigured(checkPlatform('tiktok'));
	}, [checkPlatform]);

	useEffect(() => {
		const handleOAuthConnected = (event: CustomEvent) => {
			const { platform, userProfile } = event.detail;
			if (platform === 'tiktok') {
				onConnect(userProfile.username);
			}
		};

		const handleOAuthDisconnected = (event: CustomEvent) => {
			const { platform } = event.detail;
			if (platform === 'tiktok') {
				onDisconnect();
			}
		};

		window.addEventListener('oauth_connected', handleOAuthConnected as EventListener);
		window.addEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);

		return () => {
			window.removeEventListener('oauth_connected', handleOAuthConnected as EventListener);
			window.removeEventListener('oauth_disconnected', handleOAuthDisconnected as EventListener);
		};
	}, [onConnect, onDisconnect]);

	const handleConnect = async () => {
		if (!isPlatformConfigured) {
			toast.error("TikTok OAuth is not configured. Please check your environment variables.");
			return;
		}

		try {
			await connect('tiktok');
		} catch (error) {
			console.error("TikTok connection error:", error);
			// Error is already handled in the hook
		}
	};

	const handleManualConnect = async () => {
		if (!tiktokUsername.trim()) {
			toast.error("Please enter your TikTok username");
			return;
		}

		onConnect(tiktokUsername);
		setIsModalOpen(false);
		toast.success("TikTok profile connected manually!");
	};

	const handleDisconnect = () => {
		disconnect('tiktok');
		onDisconnect();
	};

	const connection = getConnection('tiktok');
	const displayUsername = connection?.userProfile.displayName || value || "tiktok_user";
	const avatarUrl = connection?.userProfile.avatarUrl;

	return (
		<div>
			<Label htmlFor="tiktok" className="flex items-center gap-2 mb-1">
				<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
					<path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
				</svg>
				TikTok
			</Label>
			{connected ? (
				<div className="space-y-3">
					<div className="flex items-center gap-3 p-3 bg-muted/50 rounded-custom-paddings">
						<button
							onClick={() => window.open(`https://tiktok.com/@${value}`, '_blank')}
							className="flex items-center gap-3 flex-1 hover:opacity-80 transition-opacity text-left"
						>
							<Avatar className="w-8 h-8">
								<AvatarImage src={avatarUrl} alt={displayUsername} />
								<AvatarFallback>
									<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
										<path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
									</svg>
								</AvatarFallback>
							</Avatar>
							<div className="flex-1 overflow-hidden">
								<p className="font-medium text-sm truncate">{displayUsername}</p>
								<div className="flex items-center gap-2 mt-0.5">
									<Badge variant="default" className="h-4 text-[10px] px-1 font-normal bg-pink-500 hover:bg-pink-600">
										Connected
									</Badge>
								</div>
							</div>
						</button>

						<Button
							variant="destructive"
							size="sm"
							onClick={handleDisconnect}
							disabled={loading}
							className="h-8 px-2"
						>
							<X className="w-4 h-4" />
						</Button>
					</div>
				</div>
			) : (
				<div className="space-y-2">
					<p className="text-sm text-muted-foreground">
						Connect your TikTok account to automatically retrieve your information
					</p>
					<div className="flex justify-center">
						<span className="flex items-center gap-2 text-sm text-muted-foreground p-2 px-4 border rounded-md w-full justify-center bg-muted/50">
							<Lock className="w-4 h-4" /> Coming soon
						</span>
					</div>
				</div>
			)}
		</div>
	);
}
